// Thin wrapper around the backend REST API. All calls go through axiosInstance,
// which attaches the Clerk bearer token automatically when signed in.
import axiosInstance from '../utils/axiosInstance';

// ---- Tickets ----
export async function fetchTickets(params = {}) {
  const { data } = await axiosInstance.get('api/tickets/', { params });
  return data;
}

export async function fetchStats() {
  const { data } = await axiosInstance.get('api/tickets/stats/');
  return data;
}

export async function fetchAnalytics() {
  const { data } = await axiosInstance.get('api/tickets/analytics/');
  return data;
}

export async function fetchTicket(id) {
  const { data } = await axiosInstance.get(`api/tickets/${id}/`);
  return data;
}

export async function createTicket(payload) {
  const { data } = await axiosInstance.post('api/tickets/', payload);
  return data;
}

// ---- Comments / thread ----
export async function fetchComments(ticketId) {
  const { data } = await axiosInstance.get(`api/tickets/${ticketId}/comments/`);
  return data;
}

export async function addComment(ticketId, body) {
  const { data } = await axiosInstance.post(`api/tickets/${ticketId}/comments/`, { body });
  return data;
}

export async function updateTicket(id, payload) {
  const { data } = await axiosInstance.patch(`api/tickets/${id}/`, payload);
  return data;
}

// Move a ticket to Validation with a note (saved as a comment + sent on Slack)
// and optional screenshots (attached + included in the DM).
// payload: { message, position?, images?: File[] }.
export async function requestValidation(id, { message = '', position, images = [] } = {}) {
  let body;
  if (images.length) {
    body = new FormData();
    body.append('message', message);
    if (position != null) body.append('position', String(position));
    images.forEach((file) => body.append('images', file));
  } else {
    body = position != null ? { message, position } : { message };
  }
  const { data } = await axiosInstance.post(`api/tickets/${id}/request_validation/`, body);
  return data;
}

export async function deleteTicket(id) {
  await axiosInstance.delete(`api/tickets/${id}/`);
}

// Assign the ticket to the current user ("take it").
export async function assignTicketToMe(id) {
  const { data } = await axiosInstance.post(`api/tickets/${id}/assign_me/`);
  return data;
}

// ---- Members / people (users) ----
// Returns all users, each flagged is_external / is_assignable.
export async function fetchMembers() {
  const { data } = await axiosInstance.get('api/members/');
  return data;
}

// Create a lightweight external requester (a user without a login).
export async function createExternalMember(name, email = '') {
  const { data } = await axiosInstance.post('api/members/', { name, email });
  return data;
}

// ---- Attachments ----
export async function uploadAttachments(id, images = []) {
  const fd = new FormData();
  images.forEach((file) => fd.append('images', file));
  const { data } = await axiosInstance.post(`api/tickets/${id}/attachments/`, fd);
  return data;
}

export async function deleteAttachment(ticketId, attachmentId) {
  await axiosInstance.delete(`api/tickets/${ticketId}/attachments/${attachmentId}/`);
}

// ---- Notifications ----
export async function fetchNotifications() {
  const { data } = await axiosInstance.get('api/notifications/');
  return data; // { results, unread }
}

export async function markNotificationsRead(ids) {
  await axiosInstance.post('api/notifications/mark-read/', ids ? { ids } : {});
}

// ---- Public submission (no auth) ----
// Anyone with the /submit link can file a ticket without logging in.
export async function submitPublicTicket(payload, images = []) {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ''));
  images.forEach((file) => fd.append('images', file));
  const { data } = await axiosInstance.post('api/public/submit/', fd);
  return data;
}

// ---- Termy assistant ----
// Turn a natural-language message (+ optional images) into a ticket draft.
export async function assistantDraft(message, images = []) {
  const fd = new FormData();
  fd.append('message', message || '');
  images.forEach((file) => fd.append('images', file));
  const { data } = await axiosInstance.post('api/assistant/', fd);
  return data;
}

// ---- Avatars (team page) ----
export async function setAvatarCritter(userId, critter) {
  const { data } = await axiosInstance.post(`api/users/${userId}/avatar/`, { critter });
  return data;
}

export async function uploadAvatarImage(userId, file) {
  const fd = new FormData();
  fd.append('image', file);
  const { data } = await axiosInstance.post(`api/users/${userId}/avatar/`, fd);
  return data;
}

// ---- Personal API tokens (integrations / MCP) ----
export async function fetchApiTokens() {
  const { data } = await axiosInstance.get('api/tokens/');
  return data;
}

// Returns the token record plus `key` — the raw key, shown only this once.
export async function createApiToken(name) {
  const { data } = await axiosInstance.post('api/tokens/', { name });
  return data;
}

export async function deleteApiToken(id) {
  await axiosInstance.delete(`api/tokens/${id}/`);
}

// Show an existing token's key again (browser session only). 409 when the
// token predates reveal or the app secret changed — revoke and recreate.
export async function revealApiToken(id) {
  const { data } = await axiosInstance.post(`api/tokens/${id}/reveal/`);
  return data; // { id, name, key }
}
